import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputText as PrimeInputText } from "primereact/inputtext";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import useToast from "../../components/common/useToast";
import apiFunctions from "../../services/api-service";
import { AxiosError } from "axios";
import { errorHandler, formatDateTime } from "../../utils/HelperFunctions";
import "../../assets/css/datatable.css";
import DeleteConfirmation from "../../components/modal-popup/delete-popup";
import BazarProduct from "../../models/bazarProduct";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Paginator, PaginatorCurrentPageReportOptions, PaginatorRowsPerPageDropdownOptions } from "primereact/paginator";
import { Skeleton } from "primereact/skeleton";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Calendar } from "primereact/calendar";
import { MultiSelect } from "primereact/multiselect";
import { BellIcon, RefreshCw, Users } from "lucide-react";
import { UserIcon } from "@heroicons/react/24/outline";
import WhatsAppTemplateSection from "../../components/common/WhatsAppTemplateSection";
import WhatsAppIcon from "../../components/common/WhatsAppIcon";
import ConfirmActionPopup from "../../components/modal-popup/confirm-action-popup";

interface ProfilePageProps {
    listEndpoint?: string;
    pageTitle?: string;
}

const ProfilePage: React.FC<ProfilePageProps> = ({ listEndpoint = "/user/list", pageTitle = "Profiles" }) => {
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const [profileData, setProfileData] = useState<any[]>([]);
    const [dataLoad, setDataLoad] = useState(true);
    const [load, setLoad] = useState(true);
    const initialized = useRef(false);
    const [itemsPerPage, setItemsPerPage] = useState<any>(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [skipData, setSkipData] = useState(0);
    const [totalRecords, settotalRecords] = useState(0);
    const [totalPages, settotalPages] = useState<any>(0);
    const { showToast }: any = useToast();
    const [companyToDelete, setCompanyToDelete] = useState<BazarProduct | null>(null)
    const [name, setName] = useState<string>("");
    const [sortField, setSortField] = useState<string>("");
    const [sortOrder, setSortOrder] = useState<any>(-1);
    const [sortOrderapi, setSortOrderApi] = useState<string>("desc");
    const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);
    // "" = no filter — everyone. Otherwise, only users last active that many
    // days ago (or longer) — for finding long-dormant profiles.
    const [inactiveDays, setInactiveDays] = useState<string>("");
    const inactiveDaysOptions = [
        { label: "All Users", value: "" },
        { label: "Inactive 7+ days", value: "7" },
        { label: "Inactive 30+ days", value: "30" },
        { label: "Inactive 90+ days", value: "90" },
        { label: "Inactive 180+ days", value: "180" },
    ];

    const [sendType, setSendType] = useState<"instant" | "schedule">("instant");

    const [scheduleType, setScheduleType] = useState<string>("instant");

    const [scheduledDate, setScheduledDate] = useState<any>(null);

    const [scheduledTime, setScheduledTime] = useState<string>("");

    const [monthDate, setMonthDate] = useState<number>(1);

    const [specificDays, setSpecificDays] = useState<string[]>([]);

    const [dayTimes, setDayTimes] = useState<{ day: string; time: string }[]>([]);

    const [selectAllUsers, setSelectAllUsers] = useState(false);

    const startIndex = (currentPage - 1) * itemsPerPage;

    const [showMessageDialog, setShowMessageDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
    const [sendReport, setSendReport] = useState<any>(null);

    const [waTemplate, setWaTemplate] = useState("");
    const [waParams, setWaParams] = useState<string[]>([]);
    const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
    const [waConfirmVisible, setWaConfirmVisible] = useState(false);
    const [waRecipientCount, setWaRecipientCount] = useState<number | null>(null);
    const [waPendingPayload, setWaPendingPayload] = useState<any>(null);

    const [refreshing, setRefreshing] = useState(false);

    const sendTypeOptions = [
        { label: "Send Now", value: "instant" },
        { label: "Schedule", value: "schedule" }
    ];

    const scheduleTypeOptions = [
        { label: "One Time", value: "once" },
        { label: "Daily", value: "daily" },
        { label: "Weekly", value: "weekly" },
        { label: "Monthly", value: "monthly" },
        { label: "Specific Days", value: "specific_days" },
        { label: "Specific Days & Times", value: "day_time" }
    ];

    const weekDays = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday"
    ];

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const openBulkMessageDialog = (sendToAll = false) => {

        setSelectAllUsers(sendToAll);

        setSelectedUser(null);

        if (sendToAll) {
            setSelectedUsers([]);
        }

        setTitle("Sagar Samaj");
        setMessage("");

        setSendType("instant");
        setScheduleType("once");
        setScheduledDate(null);
        setScheduledTime("");
        setMonthDate(1);
        setSpecificDays([]);
        setDayTimes([]);

        setShowMessageDialog(true);
    };

    const actionBodyTemplate = (rowData: BazarProduct) => {
        return (
            <div className="action-icons">
                <Button label="view" outlined size="small" style={{ color: "#264164", borderColor: "#264164" }} onClick={() => handleView(rowData)} />
                {/* <Button label="Delete" outlined size="small" style={{ color: "#264164", borderColor: "#264164" }} onClick={() => handleDelete(rowData)} /> */}
            </div>
        );
    };

    async function getProfileData() {
        try {
            let url;
            setDataLoad(true);
            const sortColumn = sortField !== "" ? sortField : "createddate";
            if (name !== undefined && name !== null && name !== "" && name.trim().length > 1) {
                url = `${listEndpoint}/${skipData}/${itemsPerPage}/${sortColumn}/${sortOrderapi}/${encodeURIComponent(name.trim())}`;
            } else {
                url = `${listEndpoint}/${skipData}/${itemsPerPage}/${sortColumn}/${sortOrderapi}`;
            }
            await apiFunctions
                .get(url, { params: inactiveDays ? { inactiveDays } : {} })
                .then((response) => {
                    if (response && response.data && response.data.success) {
                        settotalRecords(response.data.totalRecords);
                        let totalpage: any = Math.ceil(
                            parseInt(response.data.totalRecords) / parseInt(itemsPerPage)
                        );
                        settotalPages(totalpage);
                        setProfileData(response.data.data);
                        // setProfileData(CountryFactory.createFromJsonArray(response.data.data));
                    } else {
                        showToast({
                            severity: "error",
                            summary: "Error",
                            detail: response.data.message,
                            life: 3000,
                        });
                    }
                })
                .catch((err: AxiosError) => {
                    errorHandler(err);
                })
                .finally(() => {
                    setDataLoad(false);
                    setLoad(false);
                });
        } catch (error) {
            console.log(error);
        }
    }

    const confirmDelete = async () => {
        if (companyToDelete) {
            try {
                await apiFunctions
                    .delete(`/user/delete/${companyToDelete.id}`)
                    .then((response) => {
                        if (response && response.data && response.data.success) {
                        } else {
                            showToast({
                                severity: "error",
                                summary: "Error",
                                detail: response.data.message,
                                life: 3000,
                            });
                        }
                    })
                    .catch((err: AxiosError) => {
                        errorHandler(err);
                    })
                    .finally(() => {
                        setDeleteConfirmationVisible(false);
                        setCompanyToDelete(null);
                        setSortField("createddate");
                        setSortOrder(-1);
                        setCurrentPage(0);
                        getProfileData();
                    });
            } catch (error) {
                console.log(error);
            }
        }
    };

    const handleView = (rowData: any) => {
        navigate(`/admin/profiles/view/${rowData.id}`)
    };

    const hideDeleteConfirmation = () => {
        setDeleteConfirmationVisible(false);
        setCompanyToDelete(null);
    };

    const onSort = (e: any) => {
        const field = e.sortField;
        const order = e.sortOrder === 1 ? "asc" : "desc";

        setSortOrder(e.sortOrder);
        setSortField(field);
        setSortOrderApi(order);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleItemsPerPageChange = (value: number) => {
        setItemsPerPage(value);
        setCurrentPage(0);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
        setCurrentPage(0);
    };

    useEffect(() => {
        setSkipData(currentPage * itemsPerPage);
    }, [currentPage, itemsPerPage]);

    useEffect(() => {
        if (!load) {
            getProfileData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [skipData, itemsPerPage, sortField, sortOrder, name, inactiveDays]);

    const handleInactiveDaysChange = (value: string) => {
        setInactiveDays(value);
        setCurrentPage(0);
    };

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            getProfileData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sendMessage = async () => {

        if (!title.trim()) {
            showToast({
                severity: "warn",
                summary: "Warning",
                detail: "Please enter a title",
                life: 3000
            });
            return;
        }

        if (!message.trim()) {
            showToast({
                severity: "warn",
                summary: "Warning",
                detail: "Please enter a message",
                life: 3000
            });
            return;
        }

        // Collect userIds
        let userIds: string[] = [];

        // Single user
        if (selectedUser?.userid) {
            userIds = [selectedUser.userid];
        }

        // Multiple users
        if (selectedUsers.length > 0) {
            userIds = selectedUsers.map(u => u.id);
        }

        const isSendToAll =
            !selectedUser && selectedUsers.length === 0;

        if (!userIds.length && !isSendToAll) {
            showToast({
                severity: "error",
                summary: "Error",
                detail: "No users selected",
                life: 3000
            });
            return;
        }

        try {
            setSending(true);

            const payload = {
                userIds,
                title,
                message,

                send_type: sendType,

                target_type: selectAllUsers
                    ? "all_users"
                    : "selected_users",

                schedule_type:
                    sendType === "schedule"
                        ? scheduleType
                        : "",

                scheduled_date:
                    scheduledDate
                        ? new Date(scheduledDate)
                        : null,

                scheduled_time: scheduledTime,

                month_date: monthDate,

                specific_days: specificDays,

                day_times: dayTimes,

                whatsapp_template: waTemplate,

                whatsapp_params: waParams
            };

            const response = await apiFunctions.post(
                "/activeusers/notifications",
                payload
            );

            if (response.data.success) {

                if (response.data.summary) {

                    const { sent, failed, total } = response.data.summary;

                    setSendReport({ sent, failed, total });

                    showToast({
                        severity: failed > 0 ? "warn" : "success",
                        summary: "Notification Report",
                        detail: `Total: ${total}, Sent: ${sent}, Failed: ${failed}`,
                        life: 5000
                    });

                }
                // If backend is background mode
                else {

                    showToast({
                        severity: "info",
                        summary: "Notification Started",
                        detail: "Notification job is running in background",
                        life: 4000
                    });

                }

                setShowMessageDialog(false);
                setTitle("");
                setMessage("");
                setSelectedUser(null);
                setSelectedUsers([]); // clear selection
                setWaTemplate("");
                setWaParams([]);

            } else {
                throw new Error(response.data.message);
            }

        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setSending(false);
        }
    };

    // Step 1: validate + figure out the recipient count, then show a
    // confirmation dialog before anything actually gets sent.
    const sendWhatsAppOnly = async () => {

        if (!waTemplate) {
            showToast({
                severity: "warn",
                summary: "Warning",
                detail: "Please select a WhatsApp template",
                life: 3000
            });
            return;
        }

        let userIds: string[] = [];

        if (selectedUser?.userid) {
            userIds = [selectedUser.userid];
        }

        if (selectedUsers.length > 0) {
            userIds = selectedUsers.map(u => u.id);
        }

        if (!userIds.length && !selectAllUsers) {
            showToast({
                severity: "error",
                summary: "Error",
                detail: "No users selected",
                life: 3000
            });
            return;
        }

        setWaPendingPayload({
            userIds,
            target_type: selectAllUsers ? "all_users" : "selected_users",
            template: waTemplate,
            params: waParams
        });

        if (selectAllUsers) {
            setWaRecipientCount(null); // unknown until fetched
            setWaConfirmVisible(true);
            try {
                const countRes = await apiFunctions.get("/activeusers/whatsapp-all-users-count");
                if (countRes.data.success) {
                    setWaRecipientCount(countRes.data.count);
                }
            } catch (err) {
                errorHandler(err as AxiosError);
            }
        } else {
            setWaRecipientCount(userIds.length);
            setWaConfirmVisible(true);
        }
    };

    // Step 2: fires only after the admin confirms the recipient count
    const confirmSendWhatsApp = async () => {
        if (!waPendingPayload) return;

        try {
            setSendingWhatsApp(true);

            const response = await apiFunctions.post(
                "/activeusers/whatsapp-notifications",
                waPendingPayload
            );

            if (response.data.success) {

                if (response.data.summary) {
                    const { total, sent, failed } = response.data.summary;
                    showToast({
                        severity: failed > 0 ? "warn" : "success",
                        summary: "WhatsApp Report",
                        detail: `Total: ${total}, Sent: ${sent}, Failed: ${failed}`,
                        life: 5000
                    });
                } else {
                    showToast({
                        severity: "info",
                        summary: "WhatsApp Started",
                        detail: "WhatsApp sending is running in background",
                        life: 4000
                    });
                }

                setShowMessageDialog(false);
                setWaTemplate("");
                setWaParams([]);
                setWaConfirmVisible(false);
                setWaPendingPayload(null);
            }

        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setSendingWhatsApp(false);
        }
    };

    const handleRefreshUsers = async () => {
        try {
            setRefreshing(true);
            await getProfileData();

            showToast({
                severity: "success",
                summary: "Refreshed",
                detail: "users loaded",
                life: 2000
            });
        } catch (err) {
            console.log(err);
        } finally {
            setRefreshing(false);
        }
    };

    const header = (
        <div className="grid grid-cols-12 gap-4 items-center table-header">

            {/* Left: Title + Last Report */}
            <div className="col-span-12 sm:col-span-5 flex flex-col gap-1">

                {/* Page Title */}
                <h2 className="card-title m-0 text-lg sm:text-xl">
                    {pageTitle}
                </h2>

                {/* Last Notification Report */}
                {sendReport && (
                    <div
                        className={`text-sm px-3 py-1 rounded w-fit border ${sendReport.failed > 0
                            ? "bg-yellow-50 text-yellow-800 border-yellow-200"
                            : "bg-green-50 text-green-800 border-green-200"
                            }`}
                    >
                        🔔 Last Notification:
                        <span className="ml-2 font-medium">
                            Total {sendReport.total},
                            Sent {sendReport.sent},
                            Failed {sendReport.failed}
                        </span>
                    </div>
                )}

            </div>

            {/* Right: Search + Actions */}
            <div className="col-span-12 sm:col-span-7 flex flex-col sm:flex-row gap-2 sm:justify-end">

                {/* Search Box */}
                <div className="search-box flex items-center gap-2 w-full sm:w-auto">
                    <span className="text-gray-600">Search:</span>

                    <PrimeInputText
                        type="text"
                        value={name}
                        className="search-input w-full sm:w-auto"
                        placeholder="Search by name, city or mobile no..."
                        onChange={handleSearchChange}
                    />
                </div>

                <Dropdown
                    value={inactiveDays}
                    options={inactiveDaysOptions}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(e) => handleInactiveDaysChange(e.value)}
                    className="w-full sm:w-auto"
                />

                {/* Send Button */}
                <div className="col-span-12 sm:col-span-7 flex justify-end gap-2">

                    <Button
                        icon={<RefreshCw size={20} strokeWidth={2.2} />}
                        className="w-11 h-11 flex items-center justify-center"
                        severity="info"
                        outlined
                        rounded
                        loading={refreshing}
                        onClick={handleRefreshUsers}
                        title="Refresh Active Users"
                    />

                    <div className="flex gap-2">

                        <Button
                            label="Send All"
                            icon={<Users size={20} strokeWidth={2.2} />}
                            className="gap-2 flex items-center justify-center"
                            severity="help"
                            onClick={() => openBulkMessageDialog(true)}
                        />

                        {selectedUsers.length > 0 && (
                            <Button
                                label={`Send (${selectedUsers.length})`}
                                icon={<BellIcon size={20} strokeWidth={2.2} />}
                                className="gap-2 flex items-center justify-center"
                                severity="success"
                                onClick={() => openBulkMessageDialog(false)}
                            />
                        )}

                    </div>

                </div>

            </div>

        </div>
    );

    const paginatoreTemplate = {
        layout: "RowsPerPageDropdown CurrentPageReport",
        RowsPerPageDropdown: (options: PaginatorRowsPerPageDropdownOptions) => {
            const dropdownOptions = [
                { label: 10, value: 10 },
                { label: 25, value: 25 },
                { label: 50, value: 50 },
                { label: 100, value: 100 },
                { label: 200, value: 200 },
            ];

            return (
                <div className="paginator-dropdown">
                    <span className="mx-1">Show</span>
                    <Dropdown
                        value={itemsPerPage}
                        options={dropdownOptions}
                        onChange={(e) => handleItemsPerPageChange(e.value)}
                    />
                </div>
            );
        },
        CurrentPageReport: (options: PaginatorCurrentPageReportOptions) => {
            return <span className="entries-text mx-1">entries</span>;
        },
    };

    return (
        <>
            <div className="dashboard-margin">
                {isMobile ? (
                    <div className="bg-white w-full overflow-x-hidden box-border" style={{ borderRadius: "8px" }}>
                        <div className="p-3 pb-2">{header}</div>
                        {!dataLoad && profileData.length > 0 && (
                            <div className="flex items-center gap-2 px-3 pb-2">
                                <Checkbox
                                    inputId="selectAllProfilesOnPage"
                                    checked={profileData.every((u: any) => selectedUsers.some((su: any) => su.id === u.id))}
                                    onChange={(e) => {
                                        if (e.checked) {
                                            setSelectedUsers((prev: any[]) => {
                                                const merged = [...prev];
                                                profileData.forEach((u: any) => {
                                                    if (!merged.some((su) => su.id === u.id)) merged.push(u);
                                                });
                                                return merged;
                                            });
                                        } else {
                                            setSelectedUsers((prev: any[]) => prev.filter((u) => !profileData.some((n: any) => n.id === u.id)));
                                        }
                                    }}
                                />
                                <label htmlFor="selectAllProfilesOnPage" className="text-xs cursor-pointer" style={{ color: "#6b7280" }}>Select all on this page</label>
                            </div>
                        )}
                        <div className="flex flex-col gap-2 px-3 pb-3">
                            {dataLoad ? (
                                Array.from({ length: itemsPerPage }, (_, i) => (
                                    <div key={i} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                        <Skeleton width="60%" height="1rem" className="mb-2" />
                                        <Skeleton width="40%" height="0.8rem" />
                                    </div>
                                ))
                            ) : profileData.length === 0 ? (
                                <div style={{ textAlign: "center", padding: "64px 0" }}>
                                    <UserIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                    <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No profiles found</p>
                                </div>
                            ) : (
                                profileData.map((rowData: any) => {
                                    const isSelected = selectedUsers.some((u: any) => u.id === rowData.id);
                                    return (
                                        <div key={rowData.id} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                            <div className="flex items-start gap-2 w-full">
                                                <Checkbox
                                                    className="mt-1 flex-shrink-0"
                                                    checked={isSelected}
                                                    onChange={(e) => {
                                                        if (e.checked) {
                                                            setSelectedUsers((prev: any[]) => [...prev, rowData]);
                                                        } else {
                                                            setSelectedUsers((prev: any[]) => prev.filter((u) => u.id !== rowData.id));
                                                        }
                                                    }}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <p className="m-0 font-semibold text-sm break-words" style={{ color: "#264164" }}>{rowData.name} {rowData.surname}</p>
                                                    <p className="m-0 text-xs break-words" style={{ color: "#9ca3af" }}>{rowData.profiletype} · {rowData.districtname || "-"}</p>
                                                </div>
                                            </div>
                                            <div className="flex flex-col gap-1 mt-2 text-xs" style={{ color: "#4b5563" }}>
                                                <div><span className="font-medium">Mobile:</span> {rowData.mobileno || "-"}</div>
                                                <div><span className="font-medium">Last Active:</span> {rowData.lastActiveAgo || "-"}{rowData.lastactivecity ? ` · ${rowData.lastactivecity}` : ""}</div>
                                                <div><span className="font-medium">Joined:</span> {formatDateTime(rowData.createddate)}</div>
                                            </div>
                                            <div className="flex justify-end gap-2 mt-2">
                                                {actionBodyTemplate(rowData)}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                ) : (
                    <DataTable
                        scrollable
                        scrollHeight="calc(100vh - 260px)"
                        style={{
                            minHeight: "calc(100vh - 185px)",
                            background: "#fff"
                        }}
                        header={header}

                        value={
                            dataLoad
                                ? Array.from({ length: itemsPerPage }, (_, i) => ({
                                    _skeletonId: i   // unique key
                                }))
                                : profileData
                        }

                        /* ✅ Selection Support */
                        selection={dataLoad ? [] : selectedUsers}
                        onSelectionChange={(e) => !dataLoad && setSelectedUsers(e.value)}
                        dataKey={dataLoad ? "_skeletonId" : "id"}

                        sortField={sortField}
                        sortOrder={sortOrder}
                        onSort={onSort}

                        resizableColumns
                        removableSort
                        rows={itemsPerPage}
                        emptyMessage={
                            <div style={{ textAlign: "center", padding: "64px 0" }}>
                                <UserIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No profiles found</p>
                            </div>
                        }
                    >
                        {dataLoad ? (

                            /* Skeleton Column While Loading */
                            <Column
                                headerStyle={{ width: "3rem" }}
                                body={() => <Skeleton width="1.2rem" height="1.2rem" />}
                            />

                        ) : (

                            /* Real Checkbox Column After Load */
                            <Column
                                selectionMode="multiple"
                                headerStyle={{ width: "3rem" }}
                            />

                        )}
                        <Column
                            field="name"
                            header="Name"
                            body={(row) => dataLoad ? <Skeleton /> : row.name}
                            sortable
                        />
                        <Column
                            field="surname"
                            header="Last Name"
                            body={(row) => dataLoad ? <Skeleton /> : row.surname}
                            sortable
                        />
                        <Column
                            field="mobileno"
                            header="Mobile Number"
                            body={(row) => dataLoad ? <Skeleton /> : row.mobileno || "-"}
                            sortable
                        />
                        <Column
                            field="lastActiveAgo"
                            header="Last Active Ago"
                            body={(row) => dataLoad ? <Skeleton /> : (
                                <>
                                    {row.lastActiveAgo || "-"}
                                    {row.lastactivecity && <div className="text-xs" style={{ color: "#9ca3af" }}>{row.lastactivecity}</div>}
                                </>
                            )}
                            sortable
                        />
                        <Column
                            field="profiletype"
                            header="Profile Type"
                            body={(row) => dataLoad ? <Skeleton /> : row.profiletype}
                            sortable
                        />
                        <Column
                            field="createddate"
                            header="Joining Date Time"
                            sortable
                            body={(rowData) => dataLoad ? <Skeleton /> : formatDateTime(rowData.createddate)}
                        />
                        <Column
                            field="districtname"
                            header="District"
                            body={(row) => dataLoad ? <Skeleton /> : (row.districtname || "-")}
                            sortable
                        />
                        <Column
                            header="Action"
                            className="action-column"
                            frozen
                            alignFrozen="right"
                            style={{ minWidth: "100px" }}
                            body={(row) => dataLoad ? <Skeleton /> : actionBodyTemplate(row)}
                        />
                    </DataTable>
                )}
                {totalRecords > 0 && <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 bg-white p-2 mt-2 sm:mt-0" style={{ borderRadius: "8px" }}>
                    {/* PrimeReact Paginator */}
                    <Paginator
                        template={paginatoreTemplate}
                        first={startIndex}
                        rows={itemsPerPage}
                        totalRecords={totalRecords}
                    />

                    {/* Custom Pagination Buttons */}
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                        <Button
                            onClick={() => handlePageChange(currentPage - 1)}
                            className="pagination-button flex-shrink-0 px-2 sm:px-4"
                            disabled={currentPage === 0}
                        >
                            <span className="sm:hidden">Prev</span>
                            <span className="hidden sm:inline">Previous</span>
                        </Button>

                        <Button className="pagination-button flex-1 min-w-0 cursor-default px-1 sm:px-4">
                            <span className="pagination-text whitespace-nowrap text-xs sm:text-sm">
                                Page {currentPage + 1} of {totalPages || 1}
                            </span>
                        </Button>

                        <Button
                            onClick={() => handlePageChange(currentPage + 1)}
                            className="pagination-button flex-shrink-0 px-2 sm:px-4"
                            disabled={currentPage + 1 === (totalPages === 0 ? 1 : totalPages)}
                        >
                            Next
                        </Button>
                    </div>
                </div>}
                <DeleteConfirmation
                    visible={deleteConfirmationVisible}
                    onHide={hideDeleteConfirmation}
                    onDelete={confirmDelete}
                    header="Confirm Delete"
                    message="Are you sure you want to delete this Data?"
                />
                <Dialog
                    header="Send Notification"
                    visible={showMessageDialog}
                    style={{ width: "90vw", maxWidth: "500px" }}
                    modal
                    draggable={false}
                    onHide={() => setShowMessageDialog(false)}
                >

                    <div className="flex flex-col gap-3">

                        <div className="font-semibold text-sm">
                            To: {
                                selectAllUsers
                                    ? "All Users"
                                    : selectedUser
                                        ? selectedUser.fullName
                                        : `${selectedUsers.length} Users`
                            }
                        </div>

                        {/* Title */}
                        <InputTextarea
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            rows={2}
                            placeholder="Enter notification title..."
                            className="w-full"
                        />

                        {/* Message */}
                        <InputTextarea
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={4}
                            placeholder="Enter notification message..."
                            className="w-full"
                        />

                        {/* Send Type */}
                        <div>
                            <label className="text-sm font-medium mb-1 block">Send Type</label>
                            <Dropdown
                                value={sendType}
                                options={sendTypeOptions}
                                onChange={(e) => setSendType(e.value)}
                                optionLabel="label"
                                optionValue="value"
                                className="w-full"
                            />
                        </div>

                        {/* Schedule Config */}
                        {sendType === "schedule" && (
                            <>
                                <div>
                                    <label className="text-sm font-medium mb-1 block">Schedule Type</label>
                                    <Dropdown
                                        value={scheduleType}
                                        options={scheduleTypeOptions}
                                        onChange={(e) => setScheduleType(e.value)}
                                        optionLabel="label"
                                        optionValue="value"
                                        className="w-full"
                                    />
                                </div>

                                {scheduleType === "once" && (
                                    <Calendar
                                        value={scheduledDate}
                                        onChange={(e) => setScheduledDate(e.value)}
                                        showTime
                                        hourFormat="24"
                                        placeholder="Select Date & Time"
                                        className="w-full"
                                    />
                                )}

                                {(scheduleType === "daily" ||
                                    scheduleType === "weekly" ||
                                    scheduleType === "monthly" ||
                                    scheduleType === "specific_days") && (
                                        <input
                                            type="time"
                                            value={scheduledTime}
                                            onChange={(e) => setScheduledTime(e.target.value)}
                                            className="p-2 border rounded w-full"
                                        />
                                    )}

                                {scheduleType === "monthly" && (
                                    <PrimeInputText
                                        value={monthDate.toString()}
                                        onChange={(e: any) => {
                                            let val = parseInt(e.target.value || "1");

                                            if (isNaN(val)) val = 1;
                                            if (val < 1) val = 1;
                                            if (val > 31) val = 31;

                                            setMonthDate(val);
                                        }}
                                        placeholder="Enter month date (1-31)"
                                        className="w-full"
                                    />
                                )}

                                {scheduleType === "specific_days" && (
                                    <MultiSelect
                                        value={specificDays}
                                        options={weekDays.map(day => ({ label: day, value: day }))}
                                        onChange={(e) => setSpecificDays(e.value)}
                                        placeholder="Select specific days"
                                        className="w-full"
                                    />
                                )}

                                {scheduleType === "day_time" && (
                                    <div className="flex flex-col gap-2 border rounded p-3">
                                        <label className="text-sm font-medium">Select days and set time for each</label>
                                        {weekDays.map(day => {
                                            const entry = dayTimes.find(d => d.day === day);
                                            return (
                                                <div key={day} className="flex items-center gap-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!entry}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setDayTimes(prev => [...prev, { day, time: "09:00" }]);
                                                            } else {
                                                                setDayTimes(prev => prev.filter(d => d.day !== day));
                                                            }
                                                        }}
                                                    />
                                                    <span className="capitalize text-sm" style={{ width: "90px" }}>{day}</span>
                                                    {entry && (
                                                        <input
                                                            type="time"
                                                            value={entry.time}
                                                            onChange={(e) =>
                                                                setDayTimes(prev => prev.map(d =>
                                                                    d.day === day ? { ...d, time: e.target.value } : d
                                                                ))
                                                            }
                                                            className="p-1 border rounded"
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        )}

                        <WhatsAppTemplateSection
                            template={waTemplate}
                            params={waParams}
                            onTemplateChange={setWaTemplate}
                            onParamsChange={setWaParams}
                        />

                        <div className="flex justify-end gap-2 mt-3 flex-wrap">
                            <Button
                                label="Cancel"
                                severity="secondary"
                                onClick={() => setShowMessageDialog(false)}
                                disabled={sending || sendingWhatsApp}
                            />

                            <Button
                                label="Send WhatsApp"
                                icon={<WhatsAppIcon size={16} />}
                                severity="success"
                                loading={sendingWhatsApp}
                                disabled={!waTemplate || sending}
                                onClick={sendWhatsAppOnly}
                            />

                            <Button
                                label={sendType === "instant" ? "Send Now" : "Schedule"}
                                icon="pi pi-send"
                                loading={sending}
                                disabled={sendingWhatsApp}
                                onClick={sendMessage}
                            />
                        </div>
                    </div>
                </Dialog>
                <ConfirmActionPopup
                    visible={waConfirmVisible}
                    onHide={() => { setWaConfirmVisible(false); setWaPendingPayload(null); }}
                    onConfirm={confirmSendWhatsApp}
                    header="Confirm WhatsApp Send"
                    confirmLabel="Send"
                    loading={sendingWhatsApp}
                    message={
                        waRecipientCount === null
                            ? "Calculating recipient count..."
                            : `This WhatsApp message will be sent to ${waRecipientCount} user${waRecipientCount === 1 ? "" : "s"}. Are you sure?`
                    }
                />
            </div>
        </>
    );
};

export default ProfilePage;
